(ns app.front.view.group.admin.permit-type-select
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui.column-select :as columns]
            [app.front.ui.select :as ui-select]
            [app.front.view.group.admin.subs :as gas]
            [core.front.event :refer [<sub]]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(def height "450px")

(rf/reg-sub ::root-config
  :<- [::gas/group-id]
  :<- [::entities/context]
  (fn [[group-id ctx] [_ selected-permit-type]]
    (when selected-permit-type
      (->> (entities/find-entities ctx {:group.config/target selected-permit-type
                                        :group.config/group  group-id})
           (filter du/is-active?)
           first))))

(defn get-child-configs [lang config]
  (->> config
       config-model/child-configs
       (filter du/is-active?)
       (config-model/sort-group-configs lang)))

(rf/reg-sub ::category-group-configs
  (fn [[_ selected-permit-type]]
    [(rf/subscribe [::root-config selected-permit-type])
     (rf/subscribe [:language])])
  (fn [[root-config language] _]
    (get-child-configs language root-config)))

(rf/reg-sub ::work-type-group-configs
  (fn [[_ selected-permit-type _]]
    [(rf/subscribe [::category-group-configs selected-permit-type])
     (rf/subscribe [:language])])
  (fn [[category-configs language] [_ _ selected-category]]
    (->> (p/find-first category-configs {:db/id selected-category})
         (get-child-configs language))))

(rf/reg-sub ::work-target-group-configs
  (fn [[_ selected-permit-type selected-category _]]
    [(rf/subscribe [::work-type-group-configs selected-permit-type selected-category])
     (rf/subscribe [:language])])
  (fn [[work-type-configs language] [_ _ _ selected-work-type]]
    (->> (p/find-first work-type-configs {:db/id selected-work-type})
         (get-child-configs language))))

(defn make-options [lang options selected-option config-id->value]
  (map (fn [{:keys [:db/id] :as option}]
         {:id        id
          :value     (get config-id->value id)
          :title     (config-model/localized-name option lang)
          :selected? (if (set? selected-option)
                       (contains? selected-option id)
                       (= id selected-option))})
       options))

(rf/reg-sub ::current-categories
  (fn [[_ _ [selected-permit-type _] _]]
    [(rf/subscribe [::category-group-configs selected-permit-type])
     (rf/subscribe [:language])
     (rf/subscribe [:tr])])
  (fn [[categories lang tr] [_ all-categories-placeholder [_ selected-category] config-id->value]]
    (cond->> (make-options lang categories selected-category config-id->value)
      ;; show "All categories" item if specified
      all-categories-placeholder (concat [{:id        all-categories-placeholder
                                           :title     (tr [all-categories-placeholder])
                                           :selected? (= selected-category all-categories-placeholder)}]))))

(rf/reg-sub ::current-work-types
  (fn [[_ [selected-permit-type selected-category _]] _]
    [(rf/subscribe [::work-type-group-configs selected-permit-type selected-category])
     (rf/subscribe [:language])])
  (fn [[work-types lang] [_ [_ _ selected-work-type] config-id->value]]
    (make-options lang work-types selected-work-type config-id->value)))

(rf/reg-sub ::current-work-targets
  (fn [[_ [selected-permit-type selected-category selected-work-type _]]]
    [(rf/subscribe [::work-target-group-configs selected-permit-type selected-category selected-work-type])
     (rf/subscribe [:language])])
  (fn [[work-targets lang] [_ [_ _ _ selected-work-target] config-id->value]]
    (make-options lang work-targets selected-work-target config-id->value)))

(defn- on-change-multi-fn [on-change
                           [selected-permit-type selected-category selected-work-type selected-work-targets]]
  #(on-change [selected-permit-type
               selected-category
               selected-work-type
               (if (contains? selected-work-targets %)
                 (disj selected-work-targets %)
                 (conj (or selected-work-targets #{}) %))]))

(defn permit-type-select
  "Selector for permit-type / category / work-type / work-target for configuration page. Uses the group.admin.subs
   data.

   `:selected-tuple` is [permit-type-keyword config-id config-id config-id]
   `:on-change` is a function that accepts a new tuple of that format
   `:config-id->value` is a map from config id to an additional value to show in the selector
   `:all-categories-placeholder` is an additional translatable keyword in the beginning of the category list
   `:selectable-permit-types` (optional) can be used to specify which permit-types are selectable.
   `:levels` set of active, selectable depths of permit-type -> category -> work-type -> work target. All are set by default"
  [{:keys [tr on-change config-id->value selected-tuple all-categories-placeholder multi-select-target? selectable-permit-types levels disabled]
    :or   {levels #{:product :category :work-type :work-target}}}]
  (let [[selected-permit-type
         selected-category
         selected-work-type]  selected-tuple
        permit-types          (or selectable-permit-types (<sub [::gas/group-permit-types]))
        categories            (<sub [::current-categories all-categories-placeholder selected-tuple config-id->value])
        work-types            (<sub [::current-work-types selected-tuple config-id->value])
        work-targets          (<sub [::current-work-targets selected-tuple config-id->value])]
    [:<>
     (when (:product levels)
       [ui-select/select {:id           "schema-type"
                          :class        "w-full max-w-xl mb-8"
                          :label-text   (tr [:application-type])
                          :value        selected-permit-type
                          :values       permit-types
                          :disabled     disabled
                          :val->str     (comp tr vector)
                          :allow-empty? false
                          :on-change    (comp on-change vector)}])
     [:div.grid.grid-cols-3.gap-4
      (when (:category levels)
        [columns/column-select (util/mmap {:title     :workspace/category
                                           :options   categories
                                           :disabled? disabled
                                           :on-change #(on-change [selected-permit-type %])}
                                          tr height)])
      (when (:work-type levels)
        [columns/column-select (util/mmap {:title       :application/work-type
                                           :empty-label :fees/not-applicable
                                           :options     work-types
                                           :disabled?   disabled
                                           :on-change   #(on-change [selected-permit-type selected-category %])}
                                          tr height)])
      (when (:work-target levels)
        [columns/column-select (util/mmap {:title       :application/work-target
                                           :empty-label (when (empty? work-types)
                                                          :fees/not-applicable)
                                           :options     work-targets
                                           :disabled?   disabled
                                           :icon-type   (when multi-select-target?
                                                          :checkbox)
                                           :on-change   (if multi-select-target?
                                                          (on-change-multi-fn on-change selected-tuple)
                                                          #(on-change [selected-permit-type
                                                                       selected-category
                                                                       selected-work-type
                                                                       %]))}
                                          tr height)])]]))
